import { useTranslation } from 'react-i18next';

import type { AppEntry } from '@/apps/useApps';
import { AppLink, isMockup } from '@/components/catalog/AppLink';

interface AppListProps {
  apps: readonly AppEntry[];
  onHide: (slug: string) => void;
}

export function AppList({ apps, onHide }: AppListProps) {
  const { t } = useTranslation();
  return (
    <ul className="divide-y divide-slate-200 overflow-hidden rounded-lg border bg-white shadow-sm">
      {apps.map((app) => (
        <li key={app.slug} className="flex items-center gap-3 px-4 py-3">
          {app.icon_url ? (
            <img src={app.icon_url} alt="" className="h-8 w-8 flex-none rounded" />
          ) : (
            <div aria-hidden className="h-8 w-8 flex-none rounded bg-appsol-50" />
          )}
          <div className="min-w-0 flex-1">
            <AppLink
              app={app}
              className="truncate font-medium text-slate-900 hover:text-appsol-900 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-appsol-500"
            >
              {app.name}
            </AppLink>
            {app.description && (
              <p className="truncate text-sm text-slate-500">{app.description}</p>
            )}
          </div>
          {isMockup(app) && (
            <span className="hidden flex-none rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 sm:inline-block">
              {t('home.card.prototype')}
            </span>
          )}
          {app.subcategory && (
            <span className="hidden flex-none rounded-full bg-appsol-50 px-2 py-0.5 text-xs font-medium text-appsol-900 sm:inline-block">
              {app.subcategory}
            </span>
          )}
          <AppLink
            app={app}
            className="hidden rounded-md border border-appsol-900 px-3 py-1.5 text-xs font-medium text-appsol-900 transition hover:bg-appsol-900 hover:text-white sm:inline-block"
          >
            {t('home.list.open')}
          </AppLink>
          <button
            type="button"
            onClick={() => onHide(app.slug)}
            aria-label={t('home.card.hideAria', { name: app.name })}
            title={t('home.card.hideTitle')}
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-appsol-500"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.477 10.477A3 3 0 0014.523 14.52M9.88 4.24A9.953 9.953 0 0112 4c5.523 0 10 4 10 8-.61 1.224-1.49 2.34-2.585 3.275M6.343 6.343C3.93 7.99 2.388 10.06 2 12c1 2 5.477 8 10 8 1.79 0 3.46-.4 4.97-1.1" />
            </svg>
          </button>
        </li>
      ))}
    </ul>
  );
}
